import {
	SCOPED_SOURCES,
	SECURITY_SCOPED_DATA_POINTS,
	type ScopedDataPoint,
	type ScopedSamples,
} from '@dealstack/shared/lib/documents/fieldBindingCatalogue.ts';
import {
	scopedFieldLabel,
	scopedFieldName,
	securityDisplayName,
} from '@dealstack/shared/lib/documents/scopedFieldNames.ts';
import { buildScopedLiquidExpression } from './bindPanel.utils.ts';
import type {
	ScopedPaletteField,
	ScopedSource,
	SecurityOptionRes,
	TransactionScope,
} from './BindPanel.types.ts';

// Moved verbatim from BindingBuilder.tsx — the Word palette needs the same
// radio-to-source rule, and two copies would drift.
export const scopedPointsFor = (transactionScope: TransactionScope): ScopedDataPoint[] =>
	SECURITY_SCOPED_DATA_POINTS.filter((point) => {
		if (point.source === 'either') {
			return true;
		}
		return transactionScope === 'in-progress'
			? point.source === 'allocations'
			: point.source === 'holdings';
	});

/** The list a radio position reads. `source: 'either'` points resolve through this too. */
export const sourceForScope = (transactionScope: TransactionScope): ScopedSource =>
	transactionScope === 'in-progress' ? 'allocations' : 'holdings';

// Re-exported so BindPanel's own modules keep one import site for the scoped vocabulary.
export { SCOPED_SOURCES };

// Moved to `packages/shared/src/lib/documents/scopedFieldNames.ts`: the field-placement PDF is
// rendered server-side and needs the same labels, and its own lookup knew only the flat
// catalogue — so it printed a scoped field's internal key at the author. Re-exported here so
// BindPanel's modules keep one import site for the scoped vocabulary.
export {
	knownFieldLabelsFor,
	knownFieldLabelsFor as scopedFieldLabels,
	schemeFieldLabel,
	schemeFieldName,
	scopedFieldLabel,
	scopedFieldName,
	securityDisplayName,
	securitySegment,
	spansIssuers,
} from '@dealstack/shared/lib/documents/scopedFieldNames.ts';

/**
 * The palette fields for one pinned security under one radio position.
 *
 * Each field carries the name it is offered under AND the binding that name means, so no
 * consumer has to re-derive either — re-deriving the name downstream is how a scheme that
 * collided across radio positions went unnoticed.
 *
 * `showIssuer` must be `spansIssuers` over the programme's securities, the same decision the
 * chip labels make, or a chip's text changes between the palette and a reopen.
 */
export const scopedPaletteFields = ({
	security,
	showIssuer,
	transactionScope,
	scopedSamples,
	inProgressLabel,
}: {
	security: SecurityOptionRes;
	showIssuer: boolean;
	transactionScope: TransactionScope;
	scopedSamples: ScopedSamples;
	inProgressLabel: string;
}): ScopedPaletteField[] => {
	const source = sourceForScope(transactionScope);

	return scopedPointsFor(transactionScope).map((point) => ({
		name: scopedFieldName(security, source, point.label),
		// The already-built expression, so the palette offers exactly what the PDF editor would
		// persist for the same choice.
		binding: buildScopedLiquidExpression({
			securityId: security.id,
			source,
			paths: [point.path],
			joinWith: 'space',
		}),
		group: point.group,
		label: scopedFieldLabel({
			securityName: securityDisplayName(security, showIssuer),
			source,
			label: point.label,
			inProgressLabel,
		}),
		sampleValue: scopedSamples[security.id]?.[source]?.[point.path] ?? '',
		firstOfList: source === 'allocations',
	}));
};
